import { z } from "zod";
import { ApiError, apiClient, parseResp, startRedirectFlow } from "@shared/api";
import {
  AccountPasskeyListSchema,
  AccountPasskeySchema,
  PasskeyOptionsSchema,
  ReauthResponseSchema,
  AccountIdentitySchema,
  AccountProfileSchema,
  AccountSessionSchema,
  ApiTokenSchema,
  ChangeEmailResponseSchema,
  CreateApiTokenResponseSchema,
  type AccountIdentity,
  type AccountPasskey,
  type AccountPasskeyList,
  type PasskeyOptions,
  type ReauthRequest,
  type ReauthResponse,
  type AccountProfile,
  type AccountSession,
  type ApiToken,
  type ChangeEmailResponse,
  type CreateApiTokenRequest,
  type CreateApiTokenResponse,
} from "@entities/session";

// Ошибки формы показывает сама форма: тост не нужен.
const quiet = { silentError: true } as const;

export type AccountPatch = {
  name?: string;
  commit_name?: string;
  commit_email?: string;
  commit_use_account_email?: boolean;
};

export type AccountIssue = { field: string; message: string };

/** 422 на PATCH /account: список полей с сообщением сервера на языке человека. */
export class AccountValidationFailed extends Error {
  readonly issues: AccountIssue[];

  constructor(issues: AccountIssue[]) {
    super("account validation failed");
    this.name = "AccountValidationFailed";
    this.issues = issues;
  }
}

const IssueListSchema = z.array(
  z.object({
    loc: z.array(z.union([z.string(), z.number()])),
    msg: z.string(),
  }),
);

const toValidationFailure = (error: unknown): unknown => {
  if (error instanceof ApiError && error.status === 422) {
    const parsed = IssueListSchema.safeParse(error.data);
    if (parsed.success) {
      return new AccountValidationFailed(
        parsed.data.map((issue) => ({
          field: String(issue.loc[issue.loc.length - 1] ?? ""),
          message: issue.msg,
        })),
      );
    }
  }
  return error;
};

export const accountApi = {
  get: (): Promise<AccountProfile> =>
    apiClient.get("/account").then(parseResp(AccountProfileSchema)),

  update: (patch: AccountPatch): Promise<AccountProfile> =>
    apiClient
      .patch("/account", patch, quiet)
      .then(parseResp(AccountProfileSchema))
      .catch((error: unknown) => {
        throw toValidationFailure(error);
      }),

  changePassword: async (params: {
    current_password: string;
    new_password: string;
    sign_out_other_sessions: boolean;
  }): Promise<void> => {
    await apiClient.post("/account/password", params, quiet);
  },

  changeEmail: (params: {
    email: string;
    password: string;
  }): Promise<ChangeEmailResponse> =>
    apiClient
      .post("/account/email", params, quiet)
      .then(parseResp(ChangeEmailResponseSchema)),

  sessions: (): Promise<AccountSession[]> =>
    apiClient
      .get("/account/sessions")
      .then(parseResp(z.array(AccountSessionSchema))),

  revokeSession: async (id: string): Promise<void> => {
    await apiClient.delete(
      `/account/sessions/${encodeURIComponent(id)}`,
      quiet,
    );
  },

  revokeOtherSessions: async (): Promise<void> => {
    await apiClient.delete("/account/sessions", quiet);
  },

  tokens: (): Promise<ApiToken[]> =>
    apiClient.get("/account/tokens").then(parseResp(z.array(ApiTokenSchema))),

  createToken: (body: CreateApiTokenRequest): Promise<CreateApiTokenResponse> =>
    apiClient
      .post("/account/tokens", body, quiet)
      .then(parseResp(CreateApiTokenResponseSchema)),

  revokeToken: async (id: string): Promise<void> => {
    await apiClient.delete(`/account/tokens/${encodeURIComponent(id)}`, quiet);
  },

  identities: (): Promise<AccountIdentity[]> =>
    apiClient
      .get("/account/identities")
      .then(parseResp(z.array(AccountIdentitySchema))),

  unlinkIdentity: async (providerId: string): Promise<void> => {
    await apiClient.delete(
      `/account/identities/${encodeURIComponent(providerId)}`,
      quiet,
    );
  },

  passkeys: (): Promise<AccountPasskeyList> =>
    apiClient
      .get("/account/passkeys")
      .then(parseResp(AccountPasskeyListSchema)),

  /** 403 reauth_required: сначала подтвердить личность (`reauth`), затем повторить. */
  passkeyOptions: (): Promise<PasskeyOptions> =>
    apiClient
      .post("/account/passkeys/options", {}, quiet)
      .then(parseResp(PasskeyOptionsSchema)),

  createPasskey: (body: {
    name: string | null;
    credential: Record<string, unknown>;
  }): Promise<AccountPasskey> =>
    apiClient
      .post("/account/passkeys", body, quiet)
      .then(parseResp(AccountPasskeySchema)),

  renamePasskey: (id: string, name: string): Promise<AccountPasskey> =>
    apiClient
      .patch(`/account/passkeys/${encodeURIComponent(id)}`, { name }, quiet)
      .then(parseResp(AccountPasskeySchema)),

  deletePasskey: async (id: string): Promise<void> => {
    await apiClient.delete(
      `/account/passkeys/${encodeURIComponent(id)}`,
      quiet,
    );
  },

  reauthOptions: (): Promise<PasskeyOptions> =>
    apiClient
      .post("/account/reauth/options", {}, quiet)
      .then(parseResp(PasskeyOptionsSchema)),

  reauth: (body: ReauthRequest): Promise<ReauthResponse> =>
    apiClient
      .post("/account/reauth", body, quiet)
      .then(parseResp(ReauthResponseSchema)),

  /** Привязка идёт переходом в браузере: сервер ведёт на страницу провайдера и возвращает в ту же сессию. */
  startLink: (providerId: string): Promise<{ redirect_url: string }> =>
    startRedirectFlow(
      `/account/identities/${encodeURIComponent(providerId)}/link`,
      quiet,
    ),
};
